/**
 * CompactContextBar - Minimal context bar for Chat welcome screen
 *
 * Shows Writing Style and Purpose inline, with Channel, Audience, Keywords,
 * and Framework in a "More" dropdown menu.
 *
 * Uses global ContentContextProvider for state management.
 */

import { useState, useRef, useCallback, type MouseEvent as ReactMouseEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Users,
  Settings2,
  Dna,
  Newspaper,
  Gem,
  X,
  Plus,
  Search,
  BookOpen,
  MoreHorizontal
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWritingStyles } from '@/hooks';
import { useContentContext } from '@/contexts/ContentContextProvider';
import { PLATFORM_CHANNELS, CONTENT_PURPOSES, TARGET_AUDIENCES } from '@/constants/channels';
import {
  WritingStyleDialog,
  PurposeDialog,
  ChannelDialog,
  SeoKeywordsDialog,
} from '@/components/dialogs/ContextDialogs';
import { TargetAudienceDialog } from '@/components/editor/TargetAudienceDialog';
import { StoryFrameworkDialog } from '@/components/repurpose/StoryFrameworkDialog';
import { ProjectBadge } from '@/components/projects/ProjectBadge';

interface CompactContextBarProps {
  /** Disable all interactions */
  disabled?: boolean;
  /** Show all context options inline (no More dropdown, no Project badge) */
  showAllInline?: boolean;
  /** Collapse all context options into a single dropdown on mobile */
  collapseOnMobile?: boolean;
  /** Require auth before allowing context setup */
  requireAuth?: boolean;
  /** Callback when auth is required */
  onRequireAuth?: () => void;
}

export function CompactContextBar({
  disabled,
  showAllInline = false,
  collapseOnMobile = false,
  requireAuth = false,
  onRequireAuth,
}: CompactContextBarProps) {
  // Use global context
  const { effectiveContext, updateContext, activeProject, setActiveProject } = useContentContext();
  const { allItems: writingStyles, personalStyles, brandProfiles } = useWritingStyles();

  // Dialog state management
  const [dialogStates, setDialogStates] = useState({
    writingStyle: false,
    purpose: false,
    channel: false,
    targetAudience: false,
    seoKeywords: false,
    storyFramework: false,
  });

  // Drag-scroll state
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Drag-scroll handlers
  const handleMouseDown = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    setIsDragging(true);
    setStartX(e.pageX - container.offsetLeft);
    setScrollLeft(container.scrollLeft);
    container.style.cursor = 'grabbing';
  }, []);

  const handleMouseMove = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    e.preventDefault();
    const container = e.currentTarget;
    const x = e.pageX - container.offsetLeft;
    const walk = (x - startX) * 1.5;
    container.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleMouseUp = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    setIsDragging(false);
    e.currentTarget.style.cursor = 'grab';
  }, []);

  const handleMouseLeave = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      setIsDragging(false);
      e.currentTarget.style.cursor = 'grab';
    }
  }, [isDragging]);

  const requireAuthIfNeeded = () => {
    if (requireAuth) {
      onRequireAuth?.();
      return true;
    }
    return false;
  };

  const guardedUpdateContext = (updates: Parameters<typeof updateContext>[0]) => {
    if (requireAuthIfNeeded()) return false;
    updateContext(updates);
    return true;
  };

  const openDialog = (dialogName: keyof typeof dialogStates) => {
    if (requireAuthIfNeeded()) return;
    setDialogStates(prev => ({ ...prev, [dialogName]: true }));
  };

  const closeDialog = (dialogName: keyof typeof dialogStates) => {
    setDialogStates(prev => ({ ...prev, [dialogName]: false }));
  };

  const handleDropdownTriggerClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
    if (!requireAuthIfNeeded()) return;
    event.preventDefault();
    event.stopPropagation();
  };

  // Find selected style - could be either personal or brand
  const selectedStyle = writingStyles.find(s =>
    s.id === effectiveContext.writingStyleId || s.id === effectiveContext.brandProfileId
  );

  // For channel, purpose, audience - check if it's a preset or custom value
  const selectedChannel = PLATFORM_CHANNELS.find(c => c.value === effectiveContext.channel);
  const selectedPurpose = CONTENT_PURPOSES.find(p => p.value === effectiveContext.purpose);
  const selectedAudience = TARGET_AUDIENCES.find(a => a.value === effectiveContext.targetAudience);

  // Display labels for custom or preset values
  const channelDisplay = selectedChannel?.label || effectiveContext.channel;
  const purposeDisplay = selectedPurpose?.label || effectiveContext.purpose;
  const audienceDisplay = selectedAudience?.label || effectiveContext.targetAudience;

  // Handle style selection from dialog
  const handleStyleSelect = (styleId: string, styleType: 'brand' | 'personal') => {
    if (requireAuthIfNeeded()) return;
    if (styleType === 'personal') {
      updateContext({ writingStyleId: styleId, brandProfileId: null });
      // Auto-populate default purpose from personal style
      const style = personalStyles.find(s => s.id === styleId);
      if (style?.default_purpose && !effectiveContext.purpose) {
        updateContext({ purpose: style.default_purpose });
      }
    } else {
      updateContext({ writingStyleId: null, brandProfileId: styleId });
      // Auto-populate defaults from brand profile
      const brand = brandProfiles.find(b => b.id === styleId);
      if (brand) {
        if (brand.default_purpose && !effectiveContext.purpose) {
          updateContext({ purpose: brand.default_purpose });
        }
        if (brand.target_audience && !effectiveContext.targetAudience) {
          updateContext({ targetAudience: brand.target_audience });
        }
      }
    }
    closeDialog('writingStyle');
  };

  // Get default audience based on selected style
  const getDefaultAudience = (): string => {
    if (effectiveContext.brandProfileId) {
      const brand = brandProfiles.find(b => b.id === effectiveContext.brandProfileId);
      return brand?.target_audience || '';
    }
    return '';
  };

  // Get default purpose based on selected style
  const getDefaultPurpose = (): string | null => {
    return null;
  };

  const handleProjectChange = (projectId: string | null) => {
    if (requireAuthIfNeeded()) return;
    if (projectId) {
      // Load project and set as active
      import('@/api/projects').then(({ getProject }) => {
        getProject(projectId).then((project) => {
          if (project) {
            setActiveProject(project);
          }
        });
      });
    } else {
      setActiveProject(null);
    }
  };

  // Determine selected style type for dialog
  const selectedStyleType = effectiveContext.writingStyleId ? 'personal' : effectiveContext.brandProfileId ? 'brand' : null;
  const selectedStyleId = effectiveContext.writingStyleId || effectiveContext.brandProfileId || '';

  // Count of additional context items set (for "More" badge)
  const moreItemsCount = [
    effectiveContext.channel,
    effectiveContext.targetAudience,
    effectiveContext.seoKeywords?.length,
    effectiveContext.storyFramework,
  ].filter(Boolean).length;

  const totalContextCount = [
    effectiveContext.writingStyleId || effectiveContext.brandProfileId,
    effectiveContext.purpose,
    effectiveContext.channel,
    effectiveContext.targetAudience,
    effectiveContext.seoKeywords?.length ? 'keywords' : null,
    effectiveContext.storyFramework,
  ].filter(Boolean).length;

  const inlineContent = (
    <div className="flex items-center justify-between gap-2 w-full">
      {/* Left side: context badges with drag-scroll */}
      <div
        ref={scrollContainerRef}
        className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto scrollbar-hide cursor-grab select-none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <span className="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0">
          <Settings2 className="h-3 w-3" />
        </span>

        {/* Writing Style - always visible */}
        {selectedStyle ? (
          <Badge
            variant="secondary"
            className="text-xs cursor-pointer gap-1 pr-1 flex-shrink-0"
            onClick={() => openDialog('writingStyle')}
          >
            <Dna className="h-3 w-3" />
            {selectedStyle.name}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                guardedUpdateContext({ writingStyleId: null, brandProfileId: null });
              }}
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openDialog('writingStyle')}
            disabled={disabled}
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
          >
            <Dna className="h-3 w-3 mr-1" />
            Writing Style +
          </Button>
        )}

        {/* Purpose - always visible */}
        {effectiveContext.purpose ? (
          <Badge
            variant="secondary"
            className="text-xs cursor-pointer gap-1 pr-1 flex-shrink-0 max-w-[140px]"
            onClick={() => openDialog('purpose')}
          >
            <Gem className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{purposeDisplay}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                guardedUpdateContext({ purpose: null });
              }}
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openDialog('purpose')}
            disabled={disabled}
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
          >
            <Gem className="h-3 w-3 mr-1" />
            Purpose +
          </Button>
        )}

        {/* Inline context options when showAllInline is true */}
        {showAllInline ? (
          <>
            {/* Channel - inline */}
            {effectiveContext.channel ? (
              <Badge
                variant="secondary"
                className="text-xs cursor-pointer gap-1 pr-1 flex-shrink-0"
                onClick={() => openDialog('channel')}
              >
                <Newspaper className="h-3 w-3" />
                <span className="truncate max-w-[80px]">{channelDisplay}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    guardedUpdateContext({ channel: null });
                  }}
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openDialog('channel')}
                disabled={disabled}
                className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
              >
                <Newspaper className="h-3 w-3 mr-1" />
                Channel +
              </Button>
            )}

            {/* Target Audience - inline */}
            {effectiveContext.targetAudience ? (
              <Badge
                variant="secondary"
                className="text-xs cursor-pointer gap-1 pr-1 flex-shrink-0"
                onClick={() => openDialog('targetAudience')}
              >
                <Users className="h-3 w-3" />
                <span className="truncate max-w-[80px]">{audienceDisplay}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    guardedUpdateContext({ targetAudience: null });
                  }}
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openDialog('targetAudience')}
                disabled={disabled}
                className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
              >
                <Users className="h-3 w-3 mr-1" />
                Audience +
              </Button>
            )}

            {/* SEO Keywords - inline */}
            {effectiveContext.seoKeywords?.length ? (
              <Badge
                variant="secondary"
                className="text-xs cursor-pointer gap-1 pr-1 flex-shrink-0"
                onClick={() => openDialog('seoKeywords')}
              >
                <Search className="h-3 w-3" />
                <span className="truncate">{effectiveContext.seoKeywords.length} keywords</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    guardedUpdateContext({ seoKeywords: null });
                  }}
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openDialog('seoKeywords')}
                disabled={disabled}
                className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
              >
                <Search className="h-3 w-3 mr-1" />
                Keywords +
              </Button>
            )}

            {/* Story Framework - inline */}
            {effectiveContext.storyFramework ? (
              <Badge
                variant="secondary"
                className="text-xs cursor-pointer gap-1 pr-1 flex-shrink-0"
                onClick={() => openDialog('storyFramework')}
              >
                <BookOpen className="h-3 w-3" />
                <span className="truncate max-w-[60px]">{effectiveContext.storyFramework.split('\n')[0]?.slice(0, 10) || 'Framework'}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    guardedUpdateContext({ storyFramework: null });
                  }}
                  disabled={disabled}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openDialog('storyFramework')}
                disabled={disabled}
                className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50"
              >
                <BookOpen className="h-3 w-3 mr-1" />
                Framework +
              </Button>
            )}
          </>
        ) : (
          /* More dropdown for Channel, Audience, Keywords, Framework */
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className={cn(
                  "h-7 text-xs gap-1 flex-shrink-0",
                  moreItemsCount > 0 && "border-primary/50"
                )}
                onClick={handleDropdownTriggerClick}
                disabled={disabled}
              >
                <MoreHorizontal className="h-3 w-3" />
                More
                {moreItemsCount > 0 && (
                  <Badge variant="secondary" className="h-4 px-1 text-[10px] ml-0.5">
                    {moreItemsCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 flex flex-col items-start p-2 space-y-1">
              {/* Channel */}
              <DropdownMenuItem className="p-0 focus:bg-transparent" onSelect={(e) => e.preventDefault()}>
                {effectiveContext.channel ? (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer gap-1 pr-1 justify-between"
                    onClick={() => openDialog('channel')}
                  >
                    <div className="flex items-center gap-1 min-w-0">
                      <Newspaper className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{channelDisplay}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        guardedUpdateContext({ channel: null });
                      }}
                      disabled={disabled}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog('channel')}
                    disabled={disabled}
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 w-full justify-start"
                  >
                    <Newspaper className="h-3 w-3 mr-1" />
                    Channel +
                  </Button>
                )}
              </DropdownMenuItem>

              {/* Target Audience */}
              <DropdownMenuItem className="p-0 focus:bg-transparent" onSelect={(e) => e.preventDefault()}>
                {effectiveContext.targetAudience ? (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer gap-1 pr-1 w-full justify-between"
                    onClick={() => openDialog('targetAudience')}
                  >
                    <div className="flex items-center gap-1 min-w-0">
                      <Users className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{audienceDisplay}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        guardedUpdateContext({ targetAudience: null });
                      }}
                      disabled={disabled}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog('targetAudience')}
                    disabled={disabled}
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 w-full justify-start"
                  >
                    <Users className="h-3 w-3 mr-1" />
                    Audience +
                  </Button>
                )}
              </DropdownMenuItem>

              {/* SEO Keywords */}
              <DropdownMenuItem className="p-0 focus:bg-transparent" onSelect={(e) => e.preventDefault()}>
                {effectiveContext.seoKeywords?.length ? (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer gap-1 pr-1 w-full justify-between"
                    onClick={() => openDialog('seoKeywords')}
                  >
                    <div className="flex items-center gap-1 min-w-0">
                      <Search className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{effectiveContext.seoKeywords.length} keywords</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        guardedUpdateContext({ seoKeywords: null });
                      }}
                      disabled={disabled}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog('seoKeywords')}
                    disabled={disabled}
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 w-full justify-start"
                  >
                    <Search className="h-3 w-3 mr-1" />
                    SEO Keywords +
                  </Button>
                )}
              </DropdownMenuItem>

              {/* Story Framework */}
              <DropdownMenuItem className="p-0 focus:bg-transparent" onSelect={(e) => e.preventDefault()}>
                {effectiveContext.storyFramework ? (
                  <Badge
                    variant="secondary"
                    className="text-xs cursor-pointer gap-1 pr-1 w-full justify-between"
                    onClick={() => openDialog('storyFramework')}
                  >
                    <div className="flex items-center gap-1 min-w-0">
                      <BookOpen className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate">{effectiveContext.storyFramework.split('\n')[0]?.slice(0, 15) || 'Framework'}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        guardedUpdateContext({ storyFramework: null });
                      }}
                      disabled={disabled}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDialog('storyFramework')}
                    disabled={disabled}
                    className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 w-full justify-start"
                  >
                    <BookOpen className="h-3 w-3 mr-1" />
                    Framework +
                  </Button>
                )}
              </DropdownMenuItem>

              {/* Clear all option if any "more" items are set */}
              {moreItemsCount > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      guardedUpdateContext({
                        channel: null,
                        targetAudience: null,
                        seoKeywords: null,
                        storyFramework: null,
                      });
                    }}
                    className="text-muted-foreground"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear additional context
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Project badge - right aligned (only when not inline mode) */}
      {!showAllInline && (
        <div className="-mr-2">
          <ProjectBadge
            selectedProjectId={activeProject?.id || null}
            className="flex-shrink-0"
            onProjectChange={handleProjectChange}
            requireAuth={requireAuth}
            onRequireAuth={onRequireAuth}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="px-3 sm:px-4 py-1.5 w-full">
      {collapseOnMobile ? (
        <>
          <div className="sm:hidden flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-7 text-xs gap-2",
                    totalContextCount > 0 && "border-primary/50"
                  )}
                  onClick={handleDropdownTriggerClick}
                  disabled={disabled}
                >
                  <Settings2 className="h-3 w-3" />
                  Context
                  {totalContextCount > 0 && (
                    <Badge variant="secondary" className="h-4 px-1 text-[10px]">
                      {totalContextCount}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 flex flex-col items-start p-2 space-y-1">
                {/* Writing Style */}
                <DropdownMenuItem className="p-0 focus:bg-transparent" onSelect={(e) => e.preventDefault()}>
                  {selectedStyle ? (
                    <Badge
                      variant="secondary"
                      className="text-xs cursor-pointer gap-1 pr-1 w-full justify-between"
                      onClick={() => openDialog('writingStyle')}
                    >
                      <div className="flex items-center gap-1 min-w-0">
                        <Dna className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{selectedStyle.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          guardedUpdateContext({ writingStyleId: null, brandProfileId: null });
                        }}
                        disabled={disabled}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDialog('writingStyle')}
                      disabled={disabled}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 w-full justify-start"
                    >
                      <Dna className="h-3 w-3 mr-1" />
                      Writing Style +
                    </Button>
                  )}
                </DropdownMenuItem>

                {/* Purpose */}
                <DropdownMenuItem className="p-0 focus:bg-transparent" onSelect={(e) => e.preventDefault()}>
                  {effectiveContext.purpose ? (
                    <Badge
                      variant="secondary"
                      className="text-xs cursor-pointer gap-1 pr-1 w-full justify-between"
                      onClick={() => openDialog('purpose')}
                    >
                      <div className="flex items-center gap-1 min-w-0">
                        <Gem className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{purposeDisplay}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          guardedUpdateContext({ purpose: null });
                        }}
                        disabled={disabled}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDialog('purpose')}
                      disabled={disabled}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 w-full justify-start"
                    >
                      <Gem className="h-3 w-3 mr-1" />
                      Purpose +
                    </Button>
                  )}
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                {/* Channel */}
                <DropdownMenuItem className="p-0 focus:bg-transparent" onSelect={(e) => e.preventDefault()}>
                  {effectiveContext.channel ? (
                    <Badge
                      variant="secondary"
                      className="text-xs cursor-pointer gap-1 pr-1 w-full justify-between"
                      onClick={() => openDialog('channel')}
                    >
                      <div className="flex items-center gap-1 min-w-0">
                        <Newspaper className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{channelDisplay}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          guardedUpdateContext({ channel: null });
                        }}
                        disabled={disabled}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDialog('channel')}
                      disabled={disabled}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 w-full justify-start"
                    >
                      <Newspaper className="h-3 w-3 mr-1" />
                      Channel +
                    </Button>
                  )}
                </DropdownMenuItem>

                {/* Target Audience */}
                <DropdownMenuItem className="p-0 focus:bg-transparent" onSelect={(e) => e.preventDefault()}>
                  {effectiveContext.targetAudience ? (
                    <Badge
                      variant="secondary"
                      className="text-xs cursor-pointer gap-1 pr-1 w-full justify-between"
                      onClick={() => openDialog('targetAudience')}
                    >
                      <div className="flex items-center gap-1 min-w-0">
                        <Users className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{audienceDisplay}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          guardedUpdateContext({ targetAudience: null });
                        }}
                        disabled={disabled}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDialog('targetAudience')}
                      disabled={disabled}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 w-full justify-start"
                    >
                      <Users className="h-3 w-3 mr-1" />
                      Audience +
                    </Button>
                  )}
                </DropdownMenuItem>

                {/* SEO Keywords */}
                <DropdownMenuItem className="p-0 focus:bg-transparent" onSelect={(e) => e.preventDefault()}>
                  {effectiveContext.seoKeywords?.length ? (
                    <Badge
                      variant="secondary"
                      className="text-xs cursor-pointer gap-1 pr-1 w-full justify-between"
                      onClick={() => openDialog('seoKeywords')}
                    >
                      <div className="flex items-center gap-1 min-w-0">
                        <Search className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{effectiveContext.seoKeywords.length} keywords</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          guardedUpdateContext({ seoKeywords: null });
                        }}
                        disabled={disabled}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDialog('seoKeywords')}
                      disabled={disabled}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 w-full justify-start"
                    >
                      <Search className="h-3 w-3 mr-1" />
                      SEO Keywords +
                    </Button>
                  )}
                </DropdownMenuItem>

                {/* Story Framework */}
                <DropdownMenuItem className="p-0 focus:bg-transparent" onSelect={(e) => e.preventDefault()}>
                  {effectiveContext.storyFramework ? (
                    <Badge
                      variant="secondary"
                      className="text-xs cursor-pointer gap-1 pr-1 w-full justify-between"
                      onClick={() => openDialog('storyFramework')}
                    >
                      <div className="flex items-center gap-1 min-w-0">
                        <BookOpen className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{effectiveContext.storyFramework.split('\n')[0]?.slice(0, 15) || 'Framework'}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          guardedUpdateContext({ storyFramework: null });
                        }}
                        disabled={disabled}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDialog('storyFramework')}
                      disabled={disabled}
                      className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground border border-dashed border-muted-foreground/30 hover:border-muted-foreground/50 w-full justify-start"
                    >
                      <BookOpen className="h-3 w-3 mr-1" />
                      Framework +
                    </Button>
                  )}
                </DropdownMenuItem>

                {totalContextCount > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => {
                        guardedUpdateContext({
                          writingStyleId: null,
                          brandProfileId: null,
                          purpose: null,
                          channel: null,
                          targetAudience: null,
                          seoKeywords: null,
                          storyFramework: null,
                        });
                      }}
                      className="text-muted-foreground"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Clear all context
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <ProjectBadge
              selectedProjectId={activeProject?.id || null}
              className="h-6 text-xs sm:hidden"
              onProjectChange={handleProjectChange}
              requireAuth={requireAuth}
              onRequireAuth={onRequireAuth}
              disabled={disabled}
            />
          </div>



          <div className="hidden sm:block">
            {inlineContent}
          </div>
        </>
      ) : (
        inlineContent
      )}

      {/* Dialogs */}
      <WritingStyleDialog
        selectedStyleId={selectedStyleId}
        selectedStyleType={selectedStyleType}
        onSelect={handleStyleSelect}
        open={dialogStates.writingStyle}
        onOpenChange={(open) => !open && closeDialog('writingStyle')}
      >
        <div></div>
      </WritingStyleDialog>

      <ChannelDialog
        channel={effectiveContext.channel || ''}
        onSave={(channel) => {
          if (!guardedUpdateContext({ channel })) return;
          closeDialog('channel');
        }}
        open={dialogStates.channel}
        onOpenChange={(open) => !open && closeDialog('channel')}
      >
        <div></div>
      </ChannelDialog>

      <PurposeDialog
        purpose={effectiveContext.purpose || ''}
        onSave={(purpose) => {
          if (!guardedUpdateContext({ purpose })) return;
          closeDialog('purpose');
        }}
        open={dialogStates.purpose}
        onOpenChange={(open) => !open && closeDialog('purpose')}
        selectedStyleId={selectedStyleId}
        selectedStyleType={selectedStyleType}
        defaultPurpose={getDefaultPurpose()}
      >
        <div></div>
      </PurposeDialog>

      <TargetAudienceDialog
        targetAudience={effectiveContext.targetAudience || ''}
        onSave={(audience) => {
          if (!guardedUpdateContext({ targetAudience: audience })) return;
          closeDialog('targetAudience');
        }}
        open={dialogStates.targetAudience}
        onOpenChange={(open) => !open && closeDialog('targetAudience')}
        defaultAudience={getDefaultAudience()}
      >
        <div></div>
      </TargetAudienceDialog>

      <SeoKeywordsDialog
        keywords={effectiveContext.seoKeywords || []}
        onSave={(keywords) => {
          if (!guardedUpdateContext({ seoKeywords: keywords })) return;
          closeDialog('seoKeywords');
        }}
        open={dialogStates.seoKeywords}
        onOpenChange={(open) => !open && closeDialog('seoKeywords')}
      >
        <div></div>
      </SeoKeywordsDialog>

      <StoryFrameworkDialog
        onSelect={(framework) => {
          if (!guardedUpdateContext({ storyFramework: framework })) return;
          closeDialog('storyFramework');
        }}
        currentInstructions={effectiveContext.storyFramework ?? undefined}
        open={dialogStates.storyFramework}
        onOpenChange={(open) => !open && closeDialog('storyFramework')}
      >
        <div></div>
      </StoryFrameworkDialog>
    </div>
  );
}
